import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import threadApi from "../../api/threadApi";
import ReplyList from "./ReplyList";
import PostForm from "./PostForm";

/**
 * MessageItem Component
 *
 * Displays individual message content with author information, timestamps,
 * like counts, and interaction buttons. Handles message interaction states.
 * Requirements: 3.5, 4.3, 7.1
 *
 * @param {Object} props - Component props
 * @param {Object} props.message - Message data object
 * @param {Object} props.currentUser - Current user data for permissions
 * @param {Function} props.onInteraction - Callback for message interactions
 * @param {boolean} props.isFirst - Whether this is the first message in list
 * @param {boolean} props.isLast - Whether this is the last message in list
 * @param {boolean} props.isHighlighted - Whether this message should be highlighted
 * @param {string} props.highlightReplyId - Reply ID to highlight within this message
 * @param {string} props.className - Additional CSS classes
 */
const MessageItem = ({
  message,
  currentUser,
  onInteraction,
  isFirst = false,
  isLast = false,
  isHighlighted = false,
  highlightReplyId,
  className = "",
}) => {
  // Local state for interaction feedback
  const [interactionState, setInteractionState] = useState({
    isLiking: false,
    isReplying: false,
    showReplyForm: false,
    likeError: null,
    replyError: null,
    replySuccess: false,
  });

  // Highlight state for notification-based highlighting
  const [highlightState, setHighlightState] = useState({
    isHighlighted: false,
    isAnimating: false,
  });

  // Ref for ReplyList to add new replies
  const replyListRef = useRef(null);

  /**
   * Handle highlight effect when message is highlighted via notification
   */
  useEffect(() => {
    if (isHighlighted) {
      // Start highlight animation
      setHighlightState({
        isHighlighted: true,
        isAnimating: true,
      });

      // Scroll to this message
      const messageElement = document.getElementById(`message-${message._id}`);
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      // Remove highlight after 2 seconds
      const timer = setTimeout(() => {
        setHighlightState({
          isHighlighted: false,
          isAnimating: true,
        });

        // Remove animation class after fade completes
        setTimeout(() => {
          setHighlightState({
            isHighlighted: false,
            isAnimating: false,
          });
        }, 500); // Match CSS transition duration
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isHighlighted, message._id]);

  /**
   * Check if current user is the message author
   */
  const isOwnMessage = useMemo(() => {
    return (
      currentUser &&
      message.author &&
      (currentUser._id === message.author._id ||
        currentUser.auth0Id === message.author.auth0Id)
    );
  }, [currentUser, message.author]);

  /**
   * Check if current user has liked this message
   */
  const isLikedByUser = useMemo(() => {
    if (!currentUser) {
      return false;
    }

    // Check if the message has the isLiked property (from API response)
    if (typeof message.isLiked === "boolean") {
      return message.isLiked;
    }

    // Check if the message has the hasUserLiked property (from API response)
    if (typeof message.hasUserLiked === "boolean") {
      return message.hasUserLiked;
    }

    // Fallback to checking the likes array
    if (Array.isArray(message.likes)) {
      return (
        message.likes.includes(currentUser._id) ||
        message.likes.includes(currentUser.auth0Id)
      );
    }

    return false;
  }, [currentUser, message.isLiked, message.hasUserLiked, message.likes]);

  /**
   * Update interaction state
   */
  const updateInteractionState = useCallback((updates) => {
    setInteractionState((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Handle like/unlike action with optimistic updates and rollback
   */
  const handleLike = useCallback(async () => {
    if (!currentUser || isOwnMessage || interactionState.isLiking) {
      return;
    }

    updateInteractionState({
      isLiking: true,
      likeError: null,
    });

    try {
      // Store original state for potential rollback
      const originalIsLiked = isLikedByUser;
      const originalLikeCount = message.likeCount;

      // Optimistic update
      const newIsLiked = !originalIsLiked;
      const newLikeCount = newIsLiked
        ? originalLikeCount + 1
        : Math.max(0, originalLikeCount - 1);

      // Notify parent immediately for optimistic update
      if (onInteraction) {
        onInteraction(message._id, "like", {
          isLiked: newIsLiked,
          likeCount: newLikeCount,
          optimistic: true,
        });
      }

      // Make actual API call
      const response = await threadApi.likeMessage(message._id);

      // Update with actual server response
      if (onInteraction) {
        onInteraction(message._id, "like", {
          isLiked: response.liked,
          likeCount: response.likeCount,
          optimistic: false,
          serverResponse: response,
        });
      }

      updateInteractionState({ isLiking: false });
    } catch (error) {
      console.error("Failed to like message:", error);

      // Rollback optimistic update
      if (onInteraction) {
        onInteraction(message._id, "like", {
          isLiked: isLikedByUser,
          likeCount: message.likeCount,
          rollback: true,
        });
      }

      // Handle specific error types
      let errorMessage = "Failed to like message";
      if (error.type === "AUTH_ERROR") {
        errorMessage = "Please log in to like messages";
      } else if (error.type === "CLIENT_ERROR" && error.status === 409) {
        errorMessage = "You cannot like your own message";
      } else if (error.type === "RATE_LIMIT_ERROR") {
        errorMessage = "You're liking too quickly. Please wait a moment.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      updateInteractionState({
        isLiking: false,
        likeError: errorMessage,
      });

      // Clear error after 5 seconds
      setTimeout(() => {
        updateInteractionState({ likeError: null });
      }, 5000);
    }
  }, [
    currentUser,
    isOwnMessage,
    isLikedByUser,
    message,
    onInteraction,
    interactionState.isLiking,
    updateInteractionState,
  ]);

  /**
   * Handle reply action
   */
  const handleReply = useCallback(() => {
    if (!currentUser) {
      return;
    }

    updateInteractionState({
      showReplyForm: !interactionState.showReplyForm,
      replyError: null,
    });

    // Notify parent that reply form is being shown
    if (onInteraction) {
      onInteraction(message._id, "reply", {
        showForm: !interactionState.showReplyForm,
      });
    }
  }, [
    currentUser,
    message._id,
    onInteraction,
    interactionState.showReplyForm,
    updateInteractionState,
  ]);

  /**
   * Handle reply submission
   */
  const handleReplySubmit = useCallback(
    async (messageData) => {
      if (!currentUser || !messageData.content.trim()) {
        return;
      }

      updateInteractionState({
        isReplying: true,
        replyError: null,
        replySuccess: false,
      });

      try {
        // Post reply to API
        const newReply = await threadApi.replyToMessage(
          message._id,
          messageData.content.trim()
        );

        // Add reply to the reply list
        if (replyListRef.current && replyListRef.current.addReply) {
          replyListRef.current.addReply(newReply);
        }

        // Update message reply count optimistically
        if (onInteraction) {
          onInteraction(message._id, "replyPosted", {
            replyCount: message.replyCount + 1,
            newReply,
          });
        }

        updateInteractionState({
          isReplying: false,
          showReplyForm: false,
          replySuccess: true,
        });

        // Clear success message after 3 seconds
        setTimeout(() => {
          updateInteractionState({ replySuccess: false });
        }, 3000);
      } catch (error) {
        console.error("Failed to post reply:", error);

        updateInteractionState({
          isReplying: false,
          replyError: error.message || "Failed to post reply",
        });
      }
    },
    [
      currentUser,
      message._id,
      message.replyCount,
      onInteraction,
      updateInteractionState,
    ]
  );

  /**
   * Handle reply form error
   */
  const handleReplyError = useCallback(
    (error) => {
      updateInteractionState({
        replyError: error.message || "Failed to post reply",
      });
    },
    [updateInteractionState]
  );

  /**
   * Handle reply interactions from ReplyList
   */
  const handleReplyInteraction = useCallback(
    (replyId, action, data) => {
      // Forward reply interactions to parent
      if (onInteraction) {
        onInteraction(replyId, `reply_${action}`, data);
      }
    },
    [onInteraction]
  );

  /**
   * Handle message menu actions (edit, delete, etc.)
   */
  const handleMenuAction = useCallback(
    (action) => {
      if (onInteraction) {
        onInteraction(message._id, action, { message });
      }
    },
    [message, onInteraction]
  );

  /**
   * Format message content with basic text processing
   */
  const formatMessageContent = useCallback((content) => {
    if (!content) return "";

    // Basic text processing - preserve line breaks
    return content.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < content.split("\n").length - 1 && <br />}
      </React.Fragment>
    ));
  }, []);

  /**
   * Get author display name
   */
  const getAuthorDisplayName = useCallback(() => {
    if (!message.author) return "Unknown User";
    return message.author.name || message.author.username || "Unknown User";
  }, [message.author]);

  /**
   * Get author profile picture URL
   */
  const getAuthorPicture = useCallback(() => {
    return message.author?.picture || null;
  }, [message.author]);

  /**
   * Render author avatar
   */
  const renderAuthorAvatar = useCallback(() => {
    const picture = getAuthorPicture();
    const displayName = getAuthorDisplayName();
    const initials = displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    if (picture) {
      return (
        <img
          src={picture}
          alt={`${displayName}'s avatar`}
          className="w-10 h-10 rounded-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
      );
    }

    return (
      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium text-sm">
        {initials}
      </div>
    );
  }, [getAuthorPicture, getAuthorDisplayName]);

  return (
    <div
      id={`message-${message._id}`}
      className={`message-item ${className} ${isFirst ? "first" : ""} ${
        isLast ? "last" : ""
      } transition-all duration-500 ease-in-out ${
        highlightState.isAnimating ? "transition-all duration-500" : ""
      }`}
      style={{
        backgroundColor: highlightState.isHighlighted
          ? "#FFF8E1"
          : "transparent", // light-gold background
        borderLeft: highlightState.isHighlighted
          ? "3px solid #f6a332"
          : "3px solid transparent", // gold border
        borderRadius: highlightState.isHighlighted ? "8px" : "0px",
        transform: "scale(1)",
        boxShadow: highlightState.isHighlighted
          ? "0 4px 12px rgba(246, 163, 50, 0.15)"
          : "none", // subtle gold shadow
      }}
    >
      <div className="flex space-x-3 p-4 hover:bg-gray-50 transition-colors">
        {/* Author Avatar */}
        <div className="flex-shrink-0">
          {renderAuthorAvatar()}
          {/* Fallback avatar (hidden by default) */}
          <div
            className="w-10 h-10 rounded-full bg-gray-300 items-center justify-center text-gray-700 font-medium text-sm"
            style={{ display: "none" }}
          >
            {getAuthorDisplayName()
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)}
          </div>
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          {/* Author Info and Timestamp */}
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-gray-900">
              {getAuthorDisplayName()}
            </span>
            {message.author?.username &&
              message.author.username !== message.author.name && (
                <span className="text-sm text-gray-500">
                  @{message.author.username}
                </span>
              )}
            <span className="text-sm text-gray-500">
              {message.displayTime || "Unknown time"}
            </span>
            {isOwnMessage && (
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                You
              </span>
            )}
          </div>

          {/* Message Text */}
          <div className="text-gray-900 mb-3 whitespace-pre-wrap break-words">
            {formatMessageContent(message.content)}
          </div>

          {/* Interaction Bar - Instagram style */}
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
            {/* Like Button */}
            <button
              onClick={handleLike}
              disabled={
                !currentUser || isOwnMessage || interactionState.isLiking
              }
              className={`flex items-center space-x-1 font-medium transition-colors ${
                !currentUser || isOwnMessage
                  ? "opacity-50 cursor-not-allowed"
                  : interactionState.isLiking
                  ? "opacity-75 cursor-wait"
                  : isLikedByUser
                  ? "text-red-600 hover:text-red-700"
                  : "text-gray-600 hover:text-red-600"
              }`}
              title={
                !currentUser
                  ? "Login to like messages"
                  : isOwnMessage
                  ? "You cannot like your own message"
                  : interactionState.isLiking
                  ? "Processing..."
                  : isLikedByUser
                  ? "Unlike this message"
                  : "Like this message"
              }
            >
              {/* Heart Icon */}
              <svg
                className={`w-4 h-4 transition-all ${
                  isLikedByUser ? "fill-current" : "fill-none"
                } ${interactionState.isLiking ? "animate-pulse" : ""}`}
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={isLikedByUser ? 0 : 2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span>
                {interactionState.isLiking
                  ? "..."
                  : isLikedByUser
                  ? "Liked"
                  : "Like"}
              </span>
            </button>

            {/* Like Count */}
            {message.likeCount > 0 && (
              <span className="font-medium">
                {message.likeCount.toLocaleString()} likes
              </span>
            )}

            {/* Reply Button */}
            <button
              onClick={handleReply}
              disabled={!currentUser}
              className={`font-medium transition-colors ${
                !currentUser
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:text-gray-800"
              }`}
              title={!currentUser ? "Login to reply" : "Reply to this message"}
            >
              Reply
            </button>

            {/* View More Replies Button - Inline */}
            {message.replyCount > 0 && (
              <button
                onClick={() => {
                  if (replyListRef.current) {
                    // Toggle the reply list expansion
                    const replyList = replyListRef.current;
                    if (replyList.handleToggle) {
                      replyList.handleToggle();
                    }
                  }
                }}
                className="font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                View {message.replyCount}{" "}
                {message.replyCount === 1 ? "reply" : "replies"}
              </button>
            )}

            {/* Message Menu (for own messages) */}
            {isOwnMessage && (
              <button
                onClick={() => handleMenuAction("menu")}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Message options"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Error Messages */}
          {interactionState.likeError && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {interactionState.likeError}
            </div>
          )}

          {interactionState.replyError && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {interactionState.replyError}
            </div>
          )}

          {/* Success Messages */}
          {interactionState.replySuccess && (
            <div className="mt-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
              Reply posted successfully!
            </div>
          )}
        </div>
      </div>

      {/* Reply List */}
      {(message.replyCount > 0 || interactionState.showReplyForm) && (
        <div className="ml-13">
          <ReplyList
            ref={replyListRef}
            messageId={message._id}
            initialReplyCount={message.replyCount}
            currentUser={currentUser}
            onReplyInteraction={handleReplyInteraction}
            autoExpand={false}
            highlightReplyId={highlightReplyId}
            className="message-replies"
            hideToggleButton={true}
          />

          {/* Reply Form positioned after the reply list */}
          {interactionState.showReplyForm && (
            <div className="ml-12 mt-2 p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
              <PostForm
                threadId={message.threadId}
                parentMessageId={message._id}
                onSubmit={handleReplySubmit}
                onError={handleReplyError}
                placeholder={`Reply to ${getAuthorDisplayName()}...`}
                maxLength={500}
                disabled={interactionState.isReplying}
                autoFocus={true}
                className="reply-form"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageItem;
